import {
  ApiResponse,
  getFromServer,
  postToServer,
  patchToServer,
  BASE_URL,
} from "server";

// ==================== TYPES ====================

// Input Types
export interface CreateRequestInput {
  schoolClass: string;
  studentEmail: string;
  comment: string;
  availability?: string;
  files?: File[];
}

export interface ConfirmSlotDto {
  slotId: string;
}

export interface ValidateRequestDto {
  teacherId: string;
  adminNotes?: string;
}

export interface RejectRequestDto {
  rejectionReason: string;
}

export interface ProposeSlotsDto {
  slots: string[]; // Array of ISO date strings (start times only)
}

export interface CreateMeetingDto {
  link: string;
  notes?: string;
}

export interface UpdateMeetingLinkDto {
  link: string;
}

// Output Types
export interface RequestResponse {
  _id: string;
  studentId: string;
  studentName: string;
  studentEmail: string;
  studentClass: string;
  comment: string;
  availability?: string;
  status:
    | "pending"
    | "validated"
    | "slots_proposed"
    | "student_confirmed"
    | "meeting_scheduled"
    | "scheduled"
    | "rejected"
    | "completed";
  files?: {
    _id: string;
    filename: string;
    originalName: string;
    mimeType: string;
    size: number;
    url: string;
  }[];
  fileIds?: string[];
  teacherId?: string;
  teacherName?: string;
  meetingId?: string;
  proposedSlots?: {
    _id: string;
    startTime: string;
    endTime: string;
  }[];
  selectedSlot?: {
    _id: string;
    startTime: string;
    endTime: string;
  };
  rejectionReason?: string;
  adminNotes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MeetingResponse {
  _id: string;
  requestId: string;
  teacherId: string;
  studentId: string;
  googleMeetLink: string;
  scheduledTime: string;
  status: "scheduled" | "completed" | "cancelled";
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// ==================== REQUESTS ENDPOINTS ====================

/**
 * POST /requests
 * Create a course explanation request (Student)
 */
export const createRequest = async (
  input: CreateRequestInput,
): Promise<ApiResponse<RequestResponse>> => {
  const formData = new FormData();
  formData.append("schoolClass", input.schoolClass);
  formData.append("studentEmail", input.studentEmail);
  formData.append("comment", input.comment);
  if (input.availability?.trim()) {
    formData.append("availability", input.availability.trim());
  }

  // Ajouter les fichiers si présents
  if (input.files && input.files.length > 0) {
    input.files.forEach((file) => {
      formData.append(`files`, file, file.name);
    });
  }

  return await postToServer<FormData, RequestResponse>("/requests", formData);
};

/**
 * GET /requests/mine
 * Get my course explanation requests (Student/Teacher)
 */
export const getMyRequests = async (): Promise<
  ApiResponse<RequestResponse[]>
> => {
  return await getFromServer<RequestResponse[]>("/requests/mine");
};

/**
 * GET /requests/{id}/slots
 * Get proposed time slots for a request (Student)
 */
export const getRequestSlots = async (
  requestId: string,
): Promise<
  ApiResponse<
    {
      _id: string;
      studentRequest: string;
      professor: {
        _id: string;
        firstname: string;
        lastname: string;
        email: string;
      };
      dateTime: string;
      createdAt: string;
    }[]
  >
> => {
  return await getFromServer<
    {
      _id: string;
      studentRequest: string;
      professor: {
        _id: string;
        firstname: string;
        lastname: string;
        email: string;
      };
      dateTime: string;
      createdAt: string;
    }[]
  >(`/requests/${requestId}/slots`);
};

/**
 * PATCH /requests/{id}/confirm-slot
 * Confirm a time slot (Student)
 */
export const confirmSlot = async (
  requestId: string,
  slotId: string,
): Promise<ApiResponse<RequestResponse>> => {
  return await patchToServer<ConfirmSlotDto, RequestResponse>(
    `/requests/${requestId}/confirm-slot`,
    { slotId },
  );
};

/**
 * GET /requests/all
 * Get all course explanation requests (Startup Admin)
 */
export const getAllRequests = async (): Promise<
  ApiResponse<RequestResponse[]>
> => {
  return await getFromServer<RequestResponse[]>("/requests/all");
};

/**
 * PATCH /requests/{id}/validate
 * Validate request and assign a teacher (Startup Admin)
 */
export const validateRequest = async (
  requestId: string,
  data: ValidateRequestDto,
): Promise<ApiResponse<RequestResponse>> => {
  return await patchToServer<ValidateRequestDto, RequestResponse>(
    `/requests/${requestId}/validate`,
    data,
  );
};

/**
 * PATCH /requests/{id}/reject
 * Reject a course explanation request (Startup Admin)
 */
export const rejectRequest = async (
  requestId: string,
  data: RejectRequestDto,
): Promise<ApiResponse<RequestResponse>> => {
  return await patchToServer<RejectRequestDto, RequestResponse>(
    `/requests/${requestId}/reject`,
    data,
  );
};

/**
 * POST /requests/{id}/propose-slots
 * Propose time slots for a request (Teacher)
 */
export const proposeSlots = async (
  requestId: string,
  data: ProposeSlotsDto,
): Promise<ApiResponse<RequestResponse>> => {
  return await postToServer<ProposeSlotsDto, RequestResponse>(
    `/requests/${requestId}/propose-slots`,
    data,
  );
};

/**
 * POST /requests/{id}/meetings
 * Create meeting with Google Meet link (Teacher)
 */
export const createMeeting = async (
  requestId: string,
  data: CreateMeetingDto,
): Promise<ApiResponse<MeetingResponse>> => {
  return await postToServer<CreateMeetingDto, MeetingResponse>(
    `/requests/${requestId}/meetings`,
    data,
  );
};

/**
 * GET /requests/{id}
 * Get a request by ID
 */
export const getRequestById = async (
  requestId: string,
): Promise<ApiResponse<RequestResponse>> => {
  return await getFromServer<RequestResponse>(`/requests/${requestId}`);
};

// ==================== MEETINGS ENDPOINTS ====================

/**
 * GET /meetings/{id}
 * Get a meeting by ID
 */
export const getMeetingById = async (
  meetingId: string,
): Promise<ApiResponse<MeetingResponse>> => {
  return await getFromServer<MeetingResponse>(`/meetings/${meetingId}`);
};

/**
 * PATCH /meetings/{id}/link
 * Update Google Meet link (Teacher)
 */
export const updateMeetingLink = async (
  meetingId: string,
  data: UpdateMeetingLinkDto,
): Promise<ApiResponse<MeetingResponse>> => {
  return await patchToServer<UpdateMeetingLinkDto, MeetingResponse>(
    `/meetings/${meetingId}/link`,
    data,
  );
};

/**
 * GET /meetings/request/{requestId}
 * Get meeting for a specific request
 */
export const getMeetingByRequest = async (
  requestId: string,
): Promise<ApiResponse<MeetingResponse>> => {
  return await getFromServer<MeetingResponse>(`/meetings/request/${requestId}`);
};
